import React from "react";
import * as Yup from "yup";
import { useController, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { format, isValid } from "date-fns";
import {
  Box,
  Button,
  Grid2,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import {
  InvestmentTypeEnum,
  BookingTypeEnum,
  useExchangeRates,
  useV1ListCurrencies,
} from "@bn/api/apiv1";
import { CsvBookingRow } from "@bn/api/apiv3";
import { Modal } from "@components";
import { FormElement, renderFormElement, toString } from "@helpers";
import { getInputAdornment } from "@features/csvDrop/helpers";
import { FieldLabel } from "@features/csvDrop/FieldLabel";
import { InputFieldWithRevert } from "@features/transactionWizard/components/NumberInputFieldWithRevert";
import { stringToNumber } from "./helper";

type CsvRowWithoutId = Omit<CsvBookingRow, "id">;

interface FormFields {
  transaction_type: BookingTypeEnum;
  date: string;
  name: string;
  asset_type?: InvestmentTypeEnum;
  isin: string;
  number_of_lots: number;
  security_price: number;
  commission: number;
  tax_amount: number;
  currency: string;
  exchange_rate: number;
  // tax_and_commission_in_eur?: boolean;
}

const formFields: FormElement[] = [
  {
    name: "transaction_type",
    type: "select",
    options: Object.values(BookingTypeEnum).reduce(
      (acc, curr) => ({ ...acc, [curr]: i18next.t(`bookingTypes.${curr}`) }),
      {}
    ),
  },
  {
    name: "date",
    type: "date",
  },
  {
    name: "name",
    testId: "name-input-field",
    type: "text",
  },
  {
    name: "asset_type",
    type: "select",
    options: Object.values(InvestmentTypeEnum).reduce(
      (acc, curr) => ({ ...acc, [curr]: i18next.t(`investmentTypes.${curr}`) }),
      {}
    ),
  },
  {
    name: "isin",
    type: "text",
  },
  {
    name: "number_of_lots",
    type: "number",
  },
  {
    name: "security_price",
    type: "number",
  },
  {
    name: "commission",
    type: "number",
  },
  {
    name: "tax_amount",
    type: "number",
  },
  {
    name: "currency",
    type: "text",
  },
  // TODO: not implemented yet on the backend
  // {
  //   name: "tax_and_commission_in_eur",
  //   type: "switch",
  // },
  {
    name: "exchange_rate",
    type: "number",
  },
];

type Props = {
  parsingResult: CsvBookingRow;
  onClose: () => void;
  isOpen: boolean;
  onSave: (result: CsvBookingRow) => void;
};

export const DepotParseEditModal = ({
  parsingResult,
  onClose,
  isOpen,
  onSave,
}: Props) => {
  const { t } = useTranslation();
  const isLessSm = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );

  const { data: currencies } = useV1ListCurrencies();
  const [exchangeRateTakeoverValue, setExchangeRateTakeoverValue] =
    React.useState<number>();

  const schema = Yup.object().shape({
    number_of_lots: Yup.number()
      .nullable()
      .required(t("form.errors.required"))
      .transform(stringToNumber),
    security_price: Yup.number()
      .nullable()
      .required(t("form.errors.required"))
      .transform(stringToNumber),
    name: Yup.string().required(t("form.errors.required")).nullable(),
    date: Yup.string()
      .required(t("form.errors.required"))
      .nullable()
      .test(
        "test-date",
        t("form.errors.wrongDateFormat"),
        (value: string | null | undefined) =>
          Boolean(value) && isValid(new Date(value as string))
      ),
    isin: Yup.string().required(t("form.errors.required")).nullable(),
    currency: Yup.string().required(t("form.errors.required")).nullable(),
    commission: Yup.number()
      .nullable()
      .required(t("form.errors.required"))
      .transform(stringToNumber),
    tax_amount: Yup.number()
      .nullable()
      .required(t("form.errors.required"))
      .transform(stringToNumber),
    asset_type: Yup.string().nullable(),
    transaction_type: Yup.string()
      .required(t("form.errors.required"))
      .nullable(),
    exchange_rate: Yup.number().nullable().transform(stringToNumber),
    // tax_and_commission_in_eur: Yup.boolean().nullable(),
  });

  const { control, handleSubmit, watch, setValue } = useForm<FormFields>({
    resolver: yupResolver(schema),
    defaultValues: {
      number_of_lots: parsingResult.number_of_lots?.value,
      security_price: parsingResult.security_price?.value,
      name: parsingResult.name?.value,
      date: parsingResult.date?.value,
      isin: parsingResult.isin?.value,
      currency: parsingResult.currency?.value,
      commission: parsingResult.commission?.value ?? 0,
      tax_amount: parsingResult.tax_amount?.value ?? 0,
      asset_type: parsingResult.asset_type?.value,
      transaction_type: parsingResult.transaction_type?.value,
      exchange_rate: parsingResult.exchange_rate?.value,
      // tax_and_commission_in_eur: parsingResult.tax_and_commission_in_eur?.value,
    },
  });
  const currentCurrency = watch("currency");
  const currentAssetType = watch("asset_type");
  const currentDate = watch("date");
  // const taxAndCommissionInEur = watch("tax_and_commission_in_eur");
  const isFx = Boolean(currentCurrency) && currentCurrency !== "EUR";

  useExchangeRates(
    {
      from: "EUR",
      to: currentCurrency,
      date: isValid(currentDate)
        ? format(new Date(currentDate), "yyyy-MM-dd")
        : "",
    },
    {
      enabled: Boolean(currentDate && isFx && isValid(new Date(currentDate))),
      onSuccess: (data) => {
        setExchangeRateTakeoverValue(data.rate);
      },
      staleTime: Infinity,
      refetchOnWindowFocus: false,
    }
  );

  const { field: exchangeRateField } = useController({
    control,
    name: "exchange_rate",
    defaultValue: parsingResult.exchange_rate?.value,
  });

  const onSubmit = (data: FormFields) => {
    const keys = Object.keys(data);
    const newParsingResult = keys.reduce(
      (acc, key) => ({
        ...acc,
        [key]: {
          ...parsingResult[key as keyof CsvRowWithoutId],
          error: undefined,
          value:
            key === "date"
              ? format(new Date(data[key]), "yyyy-MM-dd")
              : data[key as keyof CsvRowWithoutId],
        },
      }),
      { ...parsingResult }
    );

    onSave(newParsingResult);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("csvImport.edit")}
      height={isLessSm ? "100%" : "auto"}
    >
      <Grid2
        container
        spacing={6}
        sx={{
          overflowY: "auto",
        }}
      >
        {formFields.map((field) => {
          if (
            (field.name === "exchange_rate" ||
              field.name === "tax_and_commission_in_eur") &&
            !isFx
          )
            return null;

          if (field.name === "currency") {
            return (
              <Grid2 key={field.name} size={isLessSm ? 12 : 6}>
                {renderFormElement(
                  "csvImport.parsingResults",
                  {
                    ...field,
                    type: "select",
                    options:
                      currencies?.currencies?.reduce(
                        (acc, currency) => ({
                          ...acc,
                          [currency.code!]: `${currency.code} - ${currency.name}`,
                        }),
                        {} as Record<string, string>
                      ) ?? [],
                  },
                  control,
                  {
                    label: (
                      <FieldLabel
                        name={field.name}
                        assetType={currentAssetType as InvestmentTypeEnum}
                        error={
                          parsingResult[field.name as keyof CsvRowWithoutId]
                            ?.error
                        }
                      />
                    ),
                  }
                )}
              </Grid2>
            );
          }

          if (field.name === "exchange_rate") {
            return (
              <Grid2 key={field.name} size={12}>
                <InputFieldWithRevert
                  fieldLabel={
                    <FieldLabel
                      name={field.name}
                      assetType={currentAssetType as InvestmentTypeEnum}
                      error={
                        parsingResult[field.name as keyof CsvRowWithoutId]
                          ?.error
                      }
                    />
                  }
                  handleTakeover={(value: string) => {
                    setValue("exchange_rate", Number(value));
                    setExchangeRateTakeoverValue(undefined);
                  }}
                  takeoverDate={new Date(currentDate)}
                  takeoverValue={exchangeRateTakeoverValue}
                  id="exchange_rate"
                  value={toString(exchangeRateField.value)}
                  handleChange={exchangeRateField.onChange}
                />
              </Grid2>
            );
          }

          return (
            <Grid2 key={field.name} size={isLessSm ? 12 : 6}>
              {renderFormElement("csvImport.parsingResults", field, control, {
                InputProps: {
                  endAdornment: (
                    <Typography variant="body1" color="textSecondary">
                      {getInputAdornment(
                        field.name,
                        currentCurrency,
                        // taxAndCommissionInEur ?? false,
                        false,
                        currentAssetType as InvestmentTypeEnum
                      )}
                    </Typography>
                  ),
                },
                DialogProps: {
                  disablePortal: true,
                  sx: { zIndex: 5000 },
                },
                label: (
                  <FieldLabel
                    name={field.name}
                    assetType={currentAssetType as InvestmentTypeEnum}
                    error={
                      parsingResult[field.name as keyof CsvRowWithoutId]?.error
                    }
                  />
                ),
              })}
            </Grid2>
          );
        })}
      </Grid2>
      <Box
        display="flex"
        alignItems="center"
        flexDirection={isLessSm ? "column" : "row"}
        justifyContent="space-between"
        width="100%"
        gap={4}
        mt={8}
      >
        <Button variant="tertiary" onClick={onClose} fullWidth={isLessSm}>
          {t("navigation.previous")}
        </Button>
        <Button
          data-testid="save-button"
          variant="primary"
          onClick={handleSubmit(onSubmit)}
          fullWidth={isLessSm}
        >
          {t("csvImport.edit")}
        </Button>
      </Box>
    </Modal>
  );
};
